import { ConfigVariables } from "adapters/ports/config-variables"
import { DetalleNegocioDTO } from "app/analista/adapters/dto/detalle-solicitud-dto";
import { useCognitoUserAttributes } from "app/login/adapters/useCognitoUserAttributes";
import { useNavigate } from "react-router-dom"
import SelectByStatus from "../select-estado/select-estados";
import { useState } from "react";
import { SolicitudesHubspotDTO } from "app/analista/adapters/dto/solicitudes-hubspot-dto";
import { filterStages } from "app/analista/page-analist/infrastructure/dealstages"
import type { FormularioRespuestaDTO } from "app/analista/scoring/adapters/dto/resultado-dto"

interface ExtendedDetailDTO extends DetalleNegocioDTO {
  scoringResult?: FormularioRespuestaDTO
  approved?: boolean
  score?: number
  resume?: Record<string, number | boolean | string>
}

interface SideBarDetalleNegocioProps {
    solicitud: ExtendedDetailDTO | null
    solicitudTabla: SolicitudesHubspotDTO | null
}

const defaultOptions = [
  { value: "standby", label: "Standby", color: "orange" },
  { value: "24016549", label: "Estudio de crédito", color: "purple" },
]

const SkeletonPlaceholder = ({ height = 'h-4', width = 'w-full' }: { height?: string, width?: string }) => (
    <div className={`bg-gray-100 rounded animate-pulse ${height} ${width}`}></div>
);

export default function SideBarDetalleNegocio({ solicitud, solicitudTabla }: SideBarDetalleNegocioProps) {
    const { nameUser } = useCognitoUserAttributes()
    const navigate = useNavigate()
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null)

    const handleBack = () => {
        navigate(-1)
    }

    const getColorFromFilterStages = (dealstageId: string): string => {
        const stage = filterStages.find(stage => stage.value === dealstageId);
        if (!stage) return "gray";
        
        switch (stage.color) {
          case "#E9D8FD": return "purple";
          case "#BEE3F8": return "blue";
          case "#FED7D7": return "red";
          case "#FEEBCB": return "orange";
          case "#C6F6D5": return "green";
          default: return "gray";
        }
    };
      
    const getOptionsForDealstage = (dealstage: string | undefined) => {
        if (!dealstage) return defaultOptions;
        
        switch (dealstage) {
          case "24016550":
            return [
                { value: "standby", label: "Standby", color: "orange" },
                { value: "24016549", label: "Estudio de crédito", color: getColorFromFilterStages("24016549") }
            ];
          case "24016549":
            return [
                { value: "standby", label: "Standby", color: "orange" },
                { value: "confirmación de condiciones", label: "Confirmación de condiciones", color: "green" },
                { value: "negado", label: "Negado", color: "red" }
            ];
          default:
            return defaultOptions;
        }
    };

    const hasScoringResult = solicitud?.scoringResult !== undefined;

    return (
        <div className="h-full flex flex-col">
            <div className="bg-white shadow-2xl rounded-[24px] w-[295px] h-full flex flex-col overflow-hidden">
                <div className="flex items-start gap-x-2 py-5 px-2">
                    <svg className="w-[24px] h-[24px] text-[#000000] cursor-pointer flex-shrink-0 self-start" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={handleBack}>
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="flex-1 space-y-1">
                        {solicitud ? (
                            <div className="text-[#390094] text-xl font-medium leading-tight">
                                {solicitud.dealname}
                            </div>
                        ) : (
                            <SkeletonPlaceholder height="h-6" width="w-3/4" />
                        )}
                        {solicitud ? (
                            <p className="text-[#4A5568] text-sm">
                                {solicitud.tipodepersona}
                            </p>
                        ) : (
                            <SkeletonPlaceholder height="h-4" width="w-1/2" />
                        )}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-2 mb-5">
                    <div className="text-[#718096] text-sm font-normal flex flex-col gap-y-3">
                        <div className="flex items-center gap-x-3">
                            <img src={ConfigVariables.IMAGEN_DOCUMENTO_NEGOCIO} alt="" className="w-5 h-5 flex-shrink-0" />
                            {solicitud ? <p>{solicitud.email}</p> : <SkeletonPlaceholder width="w-full" />}
                        </div>
                        <div className="flex items-center gap-x-3">
                            <img src={ConfigVariables.IMAGEN_TELEFONO_NEGOCIO} alt="" className="w-5 h-5 flex-shrink-0" />
                            {solicitud ? <p>{solicitud.phone}</p> : <SkeletonPlaceholder width="w-full" />}
                        </div>
                        <div className="flex items-center gap-x-3">
                            <img src={ConfigVariables.IMAGEN_DOCUMENTO_NEGOCIO} alt="" className="w-5 h-5 flex-shrink-0" />
                            {solicitud ? (
                                <p>{solicitud.tipodedocumento} {solicitud.numeroidentificacion}</p>
                            ) : (
                                <SkeletonPlaceholder width="w-full" />
                            )}
                        </div>
                        <div className="flex items-center gap-x-3">
                            <img src={ConfigVariables.IMAGEN_EDAD_NEGOCIO} alt="" className="w-[19px] h-[19px] flex-shrink-0" />
                            {solicitud ? <p>{solicitud.age}</p> : <SkeletonPlaceholder width="w-1/4" />}
                        </div>
                    </div>
                    <div className="mt-5">
                        <div className="text-[#4A5568] text-[16px] font-medium">Estado</div>
                        {solicitudTabla ? (
                            <SelectByStatus
                                options={getOptionsForDealstage(solicitudTabla.dealstage)}
                                value={selectedStatus}
                                onChange={setSelectedStatus}
                                placeholder={solicitudTabla.dealstage}
                                dealstage={solicitudTabla.dealstage}
                                hasScoringResult={hasScoringResult}
                            />
                        ) : (
                            <SkeletonPlaceholder height="h-10" width="w-full" />
                        )}
                    </div>
                    <div className="mt-7 space-y-7">
                        <div>
                            {solicitud ? (
                                <p className="text-xl font-medium text-[#390094]">COP {solicitud.amount}</p>
                            ) : (
                                <SkeletonPlaceholder height="h-6" width="w-1/2" />
                            )}
                            <span className="text-sm font-medium text-[#4A5568]">Valor solicitado</span>
                        </div>

                        <div>
                            <p className="text-md font-medium text-[#4A5568]">Distribuidor</p>
                            {solicitud ? (
                                <p className="text-sm font-normal text-[#718096]">
                                    {solicitud.nombre_del_distribuidor}
                                </p>
                            ) : (
                                <SkeletonPlaceholder height="h-4" width="w-3/4" />
                            )}
                        </div>

                        <div>
                            <p className="text-md font-medium text-[#4A5568]">Fecha de radicado</p>
                            {solicitud ? (
                                <p className="text-sm font-normal text-[#718096]">{solicitud.formattedDate}</p>
                            ) : (
                                <SkeletonPlaceholder height="h-4" width="w-1/2" />
                            )}
                        </div>
                        <div>
                            <p className="text-md font-medium text-[#4A5568]">Asignado a</p>
                            {solicitud ? (
                                <p className="text-sm font-normal text-[#718096]">
                                    {nameUser} 
                                </p>
                            ) : (
                                <SkeletonPlaceholder height="h-4" width="w-1/2" />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}